import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Upload, FileText, Download, Loader2, CheckCircle, AlertCircle, ArrowRight, MapPin } from 'lucide-react';

interface CRMImportDialogProps {
  onImportComplete: () => void;
}

type ImportFormat = 'generic' | 'hubspot' | 'mailchimp' | 'google' | 'zoho';

interface ColumnMapping {
  source: string;
  target: 'name' | 'email' | 'phone' | 'notes' | 'tags' | 'skip';
}

interface ParsedContact {
  name: string;
  email: string | null;
  phone: string | null;
  // whatsapp removed
  notes: string | null;
  tags: string[];
  isValid: boolean;
  errors: string[];
}

const formatPresets: Record<ImportFormat, { label: string; description: string; columns: Record<string, string> }> = {
  generic: {
    label: 'Generic CSV',
    description: 'Standard CSV with name, email, phone columns',
    columns: { name: 'name', email: 'email', phone: 'phone', whatsapp: 'whatsapp', notes: 'notes' }
  },
  hubspot: {
    label: 'HubSpot',
    description: 'Export from HubSpot CRM contacts',
    columns: { name: 'First Name,Last Name', email: 'Email', phone: 'Phone Number', notes: 'Notes' }
  },
  mailchimp: {
    label: 'Mailchimp',
    description: 'Export from Mailchimp audience',
    columns: { name: 'First Name,Last Name', email: 'Email Address', phone: 'Phone', notes: '' }
  },
  google: {
    label: 'Google Contacts',
    description: 'Export from Google Contacts',
    columns: { name: 'Name', email: 'E-mail 1 - Value', phone: 'Phone 1 - Value', notes: 'Notes' }
  },
  zoho: {
    label: 'Zoho CRM',
    description: 'Export from Zoho CRM contacts',
    columns: { name: 'First Name,Last Name', email: 'Email', phone: 'Phone', notes: 'Description' }
  }
};

export const CRMImportDialog = ({ onImportComplete }: CRMImportDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<ImportFormat>('generic');
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview'>('upload');
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [duplicateAction, setDuplicateAction] = useState<'skip' | 'update'>('skip');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const targetFields = [
    { value: 'name', label: 'Name', required: true },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'notes', label: 'Notes' },
    { value: 'tags', label: 'Tags' },
    { value: 'skip', label: 'Skip this column' }
  ];

  const resetState = () => {
    setCsvData('');
    setStep('upload');
    setHeaders([]);
    setColumnMappings([]);
    setParsedContacts([]);
    setImportProgress(0);
  };

  const parseCSVHeaders = (text: string): string[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 1) return [];
    return lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  };

  const autoMapColumns = (headers: string[], format: ImportFormat): ColumnMapping[] => {
    const preset = formatPresets[format].columns;
    
    return headers.map(header => {
      const lowerHeader = header.toLowerCase();
      
      // Check preset mappings first
      for (const [target, sourceColumns] of Object.entries(preset)) {
        const sources = sourceColumns.toLowerCase().split(',');
        if (sources.some(s => lowerHeader.includes(s) || s.includes(lowerHeader))) {
          return { source: header, target: target as ColumnMapping['target'] };
        }
      }
      
      // Generic matching
      if (lowerHeader.includes('name') || lowerHeader.includes('nama') || lowerHeader.includes('full')) {
        return { source: header, target: 'name' };
      }
      if (lowerHeader.includes('email') || lowerHeader.includes('mail')) {
        return { source: header, target: 'email' };
      }
      if (lowerHeader.includes('phone') || lowerHeader.includes('mobile') || lowerHeader.includes('tel')) {
        return { source: header, target: 'phone' };
      }
      // whatsapp columns mapped to phone
      if (lowerHeader.includes('whatsapp') || lowerHeader === 'wa') {
        return { source: header, target: 'phone' };
      }
      if (lowerHeader.includes('note') || lowerHeader.includes('comment') || lowerHeader.includes('description')) {
        return { source: header, target: 'notes' };
      }
      if (lowerHeader.includes('tag') || lowerHeader.includes('label') || lowerHeader.includes('category')) {
        return { source: header, target: 'tags' };
      }
      
      return { source: header, target: 'skip' };
    });
  };

  const parseContacts = (text: string, mappings: ColumnMapping[]): ParsedContact[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const contacts: ParsedContact[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      const contact: ParsedContact = {
        name: '',
        email: null,
        phone: null,
        
        notes: null,
        tags: [],
        isValid: true,
        errors: []
      };

      mappings.forEach((mapping, index) => {
        const value = values[index] || '';
        if (mapping.target === 'skip' || !value) return;

        if (mapping.target === 'name') {
          contact.name = contact.name ? `${contact.name} ${value}` : value;
        } else if (mapping.target === 'tags') {
          contact.tags = value.split(';').map(t => t.trim()).filter(Boolean);
        } else {
          contact[mapping.target] = value;
        }
      });

      // Validation
      if (!contact.name || contact.name === 'Unknown') {
        contact.errors.push('Missing name');
        contact.isValid = false;
      }
      if (!contact.email && !contact.phone) {
        contact.errors.push('No contact info (email/phone)');
        contact.isValid = false;
      }
      if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
        contact.errors.push('Invalid email format');
        contact.isValid = false;
      }


      contacts.push(contact);
    }

    return contacts;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setCsvData(text);
      const parsedHeaders = parseCSVHeaders(text);
      setHeaders(parsedHeaders);
      const mappings = autoMapColumns(parsedHeaders, selectedFormat);
      setColumnMappings(mappings);
      setStep('mapping');
    };
    reader.readAsText(file);
  };

  const handleTextPaste = () => {
    if (!csvData.trim()) return;
    const parsedHeaders = parseCSVHeaders(csvData);
    setHeaders(parsedHeaders);
    const mappings = autoMapColumns(parsedHeaders, selectedFormat);
    setColumnMappings(mappings);
    setStep('mapping');
  };

  const handleMappingChange = (index: number, target: ColumnMapping['target']) => {
    const newMappings = [...columnMappings];
    newMappings[index] = { ...newMappings[index], target };
    setColumnMappings(newMappings);
  };

  const handlePreview = () => {
    const contacts = parseContacts(csvData, columnMappings);
    setParsedContacts(contacts);
    setStep('preview');
  };

  const handleImport = async () => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'Please log in to import contacts',
        variant: 'destructive',
      });
      return;
    }

    const validContacts = parsedContacts.filter(c => c.isValid);
    if (validContacts.length === 0) {
      toast({
        title: 'Error',
        description: 'No valid contacts to import',
        variant: 'destructive',
      });
      return;
    }

    setIsImporting(true);
    setImportProgress(0);

    try {
      let imported = 0;
      let skipped = 0;
      const batchSize = 50;

      for (let i = 0; i < validContacts.length; i += batchSize) {
        const batch = validContacts.slice(i, i + batchSize);
        
        const contactsToInsert = batch.map(contact => ({
          user_id: user.id,
          name: contact.name || 'Unknown',
          email: contact.email || null,
          phone: contact.phone || null,
          notes: contact.notes || null,
          tags: contact.tags.length > 0 ? contact.tags : null,
          source: 'import',
          import_source: formatPresets[selectedFormat].label,
        }));

        if (duplicateAction === 'skip') {
          // Insert with upsert to skip duplicates
          const { data, error } = await supabase
            .from('contacts')
            .upsert(contactsToInsert, { 
              onConflict: 'user_id,email',
              ignoreDuplicates: true 
            })
            .select();

          if (error) throw error;
          imported += data?.length || 0;
          skipped += batch.length - (data?.length || 0);
        } else {
          const { error } = await supabase
            .from('contacts')
            .insert(contactsToInsert);

          if (error) throw error;
          imported += batch.length;
        }

        setImportProgress(Math.round(((i + batchSize) / validContacts.length) * 100));
      }

      // Log the import
      await supabase.from('import_logs').insert({
        user_id: user.id,
        file_name: 'CSV Import',
        format: selectedFormat,
        total_records: parsedContacts.length,
        imported_records: imported,
        skipped_records: skipped + parsedContacts.filter(c => !c.isValid).length,
        errors: parsedContacts.filter(c => !c.isValid).map(c => ({ name: c.name, errors: c.errors }))
      });

      toast({
        title: 'Import Successful',
        description: `${imported} contacts imported${skipped > 0 ? `, ${skipped} skipped` : ''}`,
      });

      resetState();
      setIsOpen(false);
      onImportComplete();
    } catch (error: any) {
      console.error('Import error:', error);
      toast({
        title: 'Import Failed',
        description: error.message || 'Failed to import contacts',
        variant: 'destructive',
      });
    } finally {
      setIsImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = 'name,email,phone,whatsapp,notes,tags\nJohn Doe,john@example.com,+2348012345678,+2348012345678,VIP customer,customer;vip\nJane Smith,jane@example.com,+2348087654321,+2348087654321,Regular customer,customer';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const validCount = parsedContacts.filter(c => c.isValid).length;
  const invalidCount = parsedContacts.filter(c => !c.isValid).length;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetState(); }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="mr-2 h-4 w-4" />
          Import Contacts
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import Contacts
          </DialogTitle>
          <DialogDescription>
            Import your existing contacts from popular CRM platforms or CSV files
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 py-4">
          {['upload', 'mapping', 'preview'].map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                step === s ? 'bg-primary text-primary-foreground' : 
                ['upload', 'mapping', 'preview'].indexOf(step) > i ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {i + 1}
              </div>
              <span className={`ml-2 text-sm ${step === s ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {s === 'upload' ? 'Upload' : s === 'mapping' ? 'Map Columns' : 'Preview'}
              </span>
              {i < 2 && <ArrowRight className="mx-3 h-4 w-4 text-muted-foreground" />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="space-y-6">
            {/* Format Selection */}
            <div className="space-y-2">
              <Label>Import Format</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(formatPresets).map(([key, preset]) => (
                  <Card 
                    key={key}
                    className={`cursor-pointer transition-all ${selectedFormat === key ? 'border-primary ring-2 ring-primary/20' : 'hover:border-muted-foreground/50'}`}
                    onClick={() => setSelectedFormat(key as ImportFormat)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2">
                        {selectedFormat === key && <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />}
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{preset.label}</p>
                          <p className="text-xs text-muted-foreground truncate">{preset.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <Tabs defaultValue="upload" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="upload">Upload File</TabsTrigger>
                <TabsTrigger value="paste">Paste CSV</TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="space-y-4">
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                  <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">
                    Upload a CSV file exported from {formatPresets[selectedFormat].label}
                  </p>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Choose File
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="paste" className="space-y-4">
                <div>
                  <Label>Paste CSV Data</Label>
                  <Textarea
                    placeholder="name,email,phone,whatsapp,notes&#10;John Doe,john@example.com,+2348012345678,+2348012345678,VIP customer"
                    value={csvData}
                    onChange={(e) => setCsvData(e.target.value)}
                    rows={8}
                    className="mt-2 font-mono text-sm"
                  />
                </div>
                {csvData && (
                  <Button onClick={handleTextPaste} className="w-full">
                    Continue to Column Mapping
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex items-center justify-between pt-4 border-t">
              <Button variant="ghost" onClick={downloadTemplate}>
                <Download className="mr-2 h-4 w-4" />
                Download Template
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 'mapping' && (
          <div className="space-y-4">
            <Alert>
              <MapPin className="h-4 w-4" />
              <AlertDescription>
                Map your CSV columns to contact fields. We've auto-detected most mappings for {formatPresets[selectedFormat].label}.
              </AlertDescription>
            </Alert>

            <div className="space-y-3 max-h-[40vh] overflow-y-auto">
              {columnMappings.map((mapping, index) => (
                <div key={index} className="flex items-center gap-4 p-3 border rounded-lg">
                  <div className="flex-1">
                    <Badge variant="outline" className="font-mono">{mapping.source}</Badge>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1">
                    <Select 
                      value={mapping.target} 
                      onValueChange={(v) => handleMappingChange(index, v as ColumnMapping['target'])}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {targetFields.map(field => (
                          <SelectItem key={field.value} value={field.value}>
                            {field.label} {field.required && '*'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button onClick={handlePreview}>
                Preview Import
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 'preview' && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">{parsedContacts.length}</p>
                  <p className="text-sm text-muted-foreground">Total Records</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{validCount}</p>
                  <p className="text-sm text-muted-foreground">Valid</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-destructive">{invalidCount}</p>
                  <p className="text-sm text-muted-foreground">Invalid</p>
                </CardContent>
              </Card>
            </div>

            {/* Duplicate Handling */}
            <div className="space-y-2">
              <Label>Duplicate Handling</Label>
              <Select value={duplicateAction} onValueChange={(v: 'skip' | 'update') => setDuplicateAction(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip duplicates (by email)</SelectItem>
                  <SelectItem value="update">Add as new contacts</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Preview Table */}
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[30vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="p-2 text-left">Status</th>
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Email</th>
                      <th className="p-2 text-left">Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedContacts.slice(0, 10).map((contact, i) => (
                      <tr key={i} className={`border-t ${!contact.isValid ? 'bg-destructive/5' : ''}`}>
                        <td className="p-2" title={contact.isValid ? '' : contact.errors.join(', ')}>
                          {contact.isValid ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          )}
                        </td>
                        <td className="p-2 truncate max-w-[150px]">{contact.name || '-'}</td>
                        <td className="p-2 truncate max-w-[150px]">{contact.email || '-'}</td>
                        <td className="p-2 truncate max-w-[100px]">{contact.phone || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedContacts.length > 10 && (
                <div className="p-2 text-center text-sm text-muted-foreground bg-muted border-t">
                  And {parsedContacts.length - 10} more contacts...
                </div>
              )}
            </div>

            {invalidCount > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {invalidCount} contacts have errors and will be skipped. Common issues: missing name or contact info.
                </AlertDescription>
              </Alert>
            )}

            {/* Progress Bar during import */}
            {isImporting && (
              <div className="space-y-2">
                <Progress value={importProgress} />
                <p className="text-sm text-center text-muted-foreground">Importing... {importProgress}%</p>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep('mapping')} disabled={isImporting}>
                Back
              </Button>
              <Button onClick={handleImport} disabled={isImporting || validCount === 0}>
                {isImporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Import {validCount} Contacts
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
